// One register item: main details, every other Smartsheet column, comments thread and files with versions.
import { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router';
import { useApp } from '../lib/app';
import { supabase } from '../lib/supabase';
import { useRows } from '../lib/useRows';
import { useLive } from '../lib/live';
import { usePresence } from '../lib/presence';
import { fmtDate, fmtDateTime, fmtMoney } from '../lib/format';
import { isClosed, itemFields, registerFor, showValue, statusLabel, STATUS, type Item } from '../lib/registers';
import { Badge, Button, Card, Empty, PageHeader } from '../ui/kit';
import { RecordDrawer } from '../ui/RecordDrawer';
import { Comments } from '../ui/Comments';
import { ItemFiles, type FileRow } from '../ui/ItemFiles';

const NONE = '00000000-0000-0000-0000-000000000000';
type Named = { id: string; name: string; short_code: string | null; aliases: string[] | null };
const norm = (s: string) => s.toLowerCase().replace(/[^a-z0-9]/g, '');

export default function ItemPage() {
  const { module, id } = useParams();
  const def = registerFor(module);
  const { siteId, can } = useApp();
  const { setRecord, editing } = usePresence();
  const [item, setItem] = useState<Item | null>(null);
  const [missing, setMissing] = useState(false);
  const [edit, setEdit] = useState(false);

  async function load() {
    const { data } = await supabase.from('items').select('*').eq('id', id ?? NONE).maybeSingle();
    if (data) setItem(data as Item);
    else setMissing(true);
  }
  useEffect(() => {
    setItem(null);
    setMissing(false);
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);
  useLive(['items'], () => void load(), { filter: `id=eq.${id}`, enabled: Boolean(id) });

  useEffect(() => {
    if (!id) return;
    setRecord(`items:${id}`);
    return () => setRecord(null);
  }, [id, setRecord]);

  const files = useRows<FileRow>(
    () =>
      supabase
        .from('files')
        .select('id, original_name, mime, size_bytes, kind, external_url, doc_key, version, is_current, comment_id, uploaded_at, uploaded_by_name, source, created_at, deleted_at')
        .eq('linked_table', 'items')
        .eq('linked_id', id ?? NONE)
        .is('deleted_at', null),
    [id],
    ['files'],
    `linked_id=eq.${id}`,
  );
  const buildings = useRows<Named>(
    () => supabase.from('buildings').select('id, name, short_code, aliases').eq('site_id', item?.site_id ?? siteId ?? NONE).is('deleted_at', null).order('sort').order('name'),
    [item?.site_id, siteId],
  );
  const orgs = useRows<Named>(() => supabase.from('organisations').select('id, name, short_code, aliases').is('deleted_at', null).order('name'), []);
  const bName = useMemo(() => new Map(buildings.rows.map((b) => [b.id, b])), [buildings.rows]);
  const oName = useMemo(() => new Map(orgs.rows.map((o) => [o.id, o])), [orgs.rows]);

  if (!def) return <Navigate to="/" replace />;
  if (missing) return <Empty>This item was not found, or you do not have access to it.</Empty>;
  if (!item) return <Empty>Loading…</Empty>;

  const editable = can(def.module, 4) && !item.deleted_at;
  const canComment = can(def.module, 2);
  const others = editing('items', item.id);
  const late = item.respond_by && item.respond_by < new Date().toISOString().slice(0, 10) && !isClosed(item.status);
  // linked names, plus any Smartsheet names that did not match a building/organisation
  const names = (ids: string[], map: Map<string, Named>, raw: string[]) => {
    const linked = ids.map((x) => map.get(x)).filter(Boolean) as Named[];
    const keys = new Set(linked.flatMap((n) => [n.name, n.short_code ?? '', ...(n.aliases ?? [])].map(norm)));
    const extra = raw.filter((r) => !keys.has(norm(r)));
    return [...linked.map((n) => n.name), ...extra].join(', ') || '—';
  };
  const data = item.data ?? {};
  const keyData = (def.keyData ?? []).filter((k) => data[k] !== undefined);
  const restData = Object.entries(data).filter(([k]) => !keyData.includes(k));

  return (
    <>
      <Link className="back" to={`/r/${def.module}`}>
        ← {def.label}
      </Link>
      <PageHeader
        title={item.number ?? def.one}
        sub={
          <span className="item-head">
            <Badge tone={STATUS[item.status]?.tone ?? 'neutral'}>{statusLabel(item.status)}</Badge>
            {item.subtype ? <Badge>{item.subtype}</Badge> : null}
            {item.ref_no ? <span>{item.ref_no}</span> : null}
            {item.deleted_at ? <Badge tone="dark">Deleted</Badge> : null}
          </span>
        }
        actions={editable ? <Button kind="primary" onClick={() => setEdit(true)}>Edit</Button> : null}
      />
      {others.length ? (
        <div className="banner banner-info" role="status">
          {others.map((o) => o.name).join(', ')} {others.length === 1 ? 'is' : 'are'} looking at this item too.
        </div>
      ) : null}

      <div className="item-layout">
        <div className="stack">
          <Card title="Details">
            <dl className="facts">
              <div className="wide">
                <dt>{def.type === 'nda' ? 'Contractor / individual' : 'Topic'}</dt>
                <dd>{item.title}</dd>
              </div>
              <div>
                <dt>Buildings</dt>
                <dd>{names(item.building_ids, bName, item.buildings_raw)}</dd>
              </div>
              {def.hasOrg ? (
                <div>
                  <dt>Contractor</dt>
                  <dd>{(item.org_id && oName.get(item.org_id)?.name) || item.org_raw || '—'}</dd>
                </div>
              ) : null}
              {def.type !== 'nda' ? (
                <div>
                  <dt>Directed to</dt>
                  <dd>{names(item.directed_to, oName, item.directed_raw)}</dd>
                </div>
              ) : null}
              {def.money ? (
                <div>
                  <dt>Amount ({def.money})</dt>
                  <dd>{fmtMoney(item.amount)}</dd>
                </div>
              ) : null}
              {def.type !== 'nda' ? (
                <div>
                  <dt>Submitted</dt>
                  <dd>{fmtDate(item.submitted_on)}</dd>
                </div>
              ) : null}
              {def.hasRespondBy ? (
                <div>
                  <dt>Respond by</dt>
                  <dd className={late ? 'overdue' : ''}>
                    {fmtDate(item.respond_by)}
                    {late ? ' · overdue' : ''}
                  </dd>
                </div>
              ) : null}
              {keyData.map((k) => (
                <div key={k}>
                  <dt>{k}</dt>
                  <dd>{showValue(data[k])}</dd>
                </div>
              ))}
              {item.notes ? (
                <div className="wide">
                  <dt>Comments / notes</dt>
                  <dd>{item.notes}</dd>
                </div>
              ) : null}
              {item.remark ? (
                <div className="wide">
                  <dt>{def.type === 'variation' ? 'Hold remarks' : 'Remark / directive'}</dt>
                  <dd>{item.remark}</dd>
                </div>
              ) : null}
              {item.response ? (
                <div className="wide">
                  <dt>Response</dt>
                  <dd>{item.response}</dd>
                </div>
              ) : null}
            </dl>
          </Card>

          <Card title="Comments">
            <Comments itemId={item.id} siteId={item.site_id} canComment={canComment && !item.deleted_at} files={files.rows} onFilesChanged={() => void files.reload()} />
          </Card>
        </div>

        <div className="stack">
          <Card title={`Files${files.rows.length ? ` (${new Set(files.rows.map((f) => f.doc_key)).size})` : ''}`}>
            <ItemFiles files={files.rows} siteId={item.site_id} itemId={item.id} canEdit={editable && can('files', 4)} onChanged={() => void files.reload()} />
          </Card>
          {restData.length ? (
            <Card title="More details">
              <dl className="facts">
                {restData.map(([k, v]) => (
                  <div key={k}>
                    <dt>{k}</dt>
                    <dd>{showValue(v)}</dd>
                  </div>
                ))}
              </dl>
            </Card>
          ) : null}
          <p className="muted small">
            {item.ss_row_id ? `Imported from Smartsheet${item.ss_created_by ? ` · first entered by ${item.ss_created_by}` : ''}${item.ss_created_at ? ` on ${fmtDateTime(item.ss_created_at)}` : ''}. ` : ''}
            Last changed {fmtDateTime(item.updated_at)}.
          </p>
        </div>
      </div>

      <RecordDrawer
        open={edit}
        onClose={() => setEdit(false)}
        title={`Edit ${item.number ?? def.one}`}
        table="items"
        fields={itemFields(def, buildings.rows, orgs.rows)}
        record={item}
        canEdit={editable}
        onSaved={() => void load()}
        label={(r) => `${def.one} ${String(r.number ?? '')}`}
      />
    </>
  );
}
